/*
 * © 2021 Thoughtworks, Inc.
 */

import React, { ReactElement, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import CloudOffIcon from '@mui/icons-material/CloudOff'
import { Grid } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { AxiosError } from 'axios'

export type ErrorState = {
  statusText?: string
  status?: string
}

export type ErrorHandlingType = {
  error: AxiosError | null
  setError: (e?: AxiosError) => void
}

export const useAxiosErrorHandling = (
  onApiError?: (e: Error) => void,
): ErrorHandlingType => {
  const [error, setError] = useState<AxiosError | null>(null)

  useEffect(() => {
    if (error && onApiError) {
      onApiError(error)
    }
  }, [error])

  return { error, setError }
}

const useStyles = makeStyles()((theme) => ({
  cloudIcon: {
    fontSize: '175px',
    color: theme.palette.primary.main,
  },
  errorStatus: {
    fontSize: '36px',
    textAlign: 'center',
  },
  errorMessage: {
    fontSize: '18px',
    textAlign: 'center',
  },
}))

const DEFAULT_ERROR = {
  status: '520',
  statusText: 'Unknown Error',
}

export const formatAxiosError = (e: AxiosError): ErrorState => {
  return e.response
    ? {
        status: e.response.status.toString(),
        statusText: e.response.statusText,
      }
    : DEFAULT_ERROR
}

interface ErrorPageProps {
  errorMessage?: string
}

const ErrorPage = (props: ErrorPageProps): ReactElement<ErrorPageProps> => {
  const location = useLocation()
  const { statusText, status } = (location.state as ErrorState) ?? DEFAULT_ERROR
  const { classes } = useStyles()

  return (
    <Grid
      container
      spacing={0}
      direction="column"
      alignItems="center"
      justifyContent="center"
      style={{ height: '100%' }}
    >
      <CloudOffIcon className={classes.cloudIcon} />
      <div data-testid="error-page">
        <h1 className={classes.errorStatus}>
          {status} {statusText}
        </h1>
        <div data-testid="error-message" className={classes.errorMessage}>
          {props.errorMessage
            ? props.errorMessage
            : 'Something has gone wrong, Please try again later'}
        </div>
      </div>
    </Grid>
  )
}

export default ErrorPage
